import React, { useMemo, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './TradeGrid.css';

/**
 * Parse a TradeNumber into a sortable tuple so that parent trades are followed
 * by their internal allocation legs:
 *   12345          → [12345, 0, 0]
 *   INT-12345.1.1  → [12345, 1, 1]
 *   INT-12345.1.2  → [12345, 1, 2]
 */
function tradeNumberSortKey(tn: string): [number, number, number] {
  if (!tn) return [Infinity, 0, 0];
  const intMatch = tn.match(/^INT-(\d+)\.(\d+)\.(\d+)$/);
  if (intMatch) {
    return [parseInt(intMatch[1], 10), parseInt(intMatch[2], 10), parseInt(intMatch[3], 10)];
  }
  const num = parseInt(tn, 10);
  if (!isNaN(num)) return [num, 0, 0];
  return [Infinity, 0, 0];
}

const tradeNumberComparator = (a: string, b: string): number => {
  const ka = tradeNumberSortKey(a);
  const kb = tradeNumberSortKey(b);
  return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
};

interface CommodityTradesGridProps {
  trades: any[];
  loading: boolean;
  multiSelect?: boolean;
  onMultiSelectionChanged?: (trades: any[]) => void;
  selectAllTrigger?: number;
  clearAllTrigger?: number;
  tradeNumberSearch?: string;
  maturityDateFrom?: string;
  showMirrorId?: boolean;
  showParentTradeNumber?: boolean;
  suppressDefaultSort?: boolean;
  showSettlementColumns?: boolean;
}

const CommodityTradesGrid: React.FC<CommodityTradesGridProps> = ({
  trades,
  loading,
  multiSelect = false,
  onMultiSelectionChanged,
  selectAllTrigger,
  clearAllTrigger,
  tradeNumberSearch,
  maturityDateFrom,
  showMirrorId = false,
  showParentTradeNumber = false,
  suppressDefaultSort = false,
  showSettlementColumns = false,
}) => {
  const { t, i18n } = useTranslation();
  const gridRef = useRef<AgGridReact>(null);

  // Refs for external filter values (avoids re-renders)
  const tradeNumberSearchRef = useRef(tradeNumberSearch);
  tradeNumberSearchRef.current = tradeNumberSearch;

  const maturityDateFromRef = useRef(maturityDateFrom);
  maturityDateFromRef.current = maturityDateFrom;

  // Notify grid when external filter values change
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [tradeNumberSearch, maturityDateFrom]);

  // Select all trigger
  useEffect(() => {
    if (selectAllTrigger && selectAllTrigger > 0 && gridRef.current?.api) {
      gridRef.current.api.selectAllFiltered();
    }
  }, [selectAllTrigger]);

  // Clear all trigger
  useEffect(() => {
    if (clearAllTrigger && clearAllTrigger > 0 && gridRef.current?.api) {
      gridRef.current.api.deselectAll();
    }
  }, [clearAllTrigger]);

  // AG Grid locale
  const localeText = useMemo(() => {
    if (i18n.language === 'es') return AG_GRID_LOCALE_ES;
    if (i18n.language === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);

  const columnDefs = useMemo<ColDef[]>(() => {
    const dateFmt = (params: any): string => {
      const v = params.value;
      if (!v || typeof v !== 'string') return v ?? '';
      const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
    };

    const numFmt = (decimals: number) => (params: any): string => {
      if (params.value == null || params.value === '') return '';
      const n = parseFloat(params.value);
      if (isNaN(n)) return String(params.value);
      return n.toLocaleString(i18n.language, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true,
      });
    };

    return [
      // Visible columns — in user-specified order
      { field: 'TradeNumber',          headerName: t('commodities.positions.grid.tradeNumber'),          pinned: 'left', width: 140, comparator: tradeNumberComparator, ...(!suppressDefaultSort ? { sort: 'asc' as const } : {}) },
      ...(showMirrorId ? [{
        field: 'MirrorId',
        headerName: t('commodities.positions.grid.mirrorId', 'Mirror ID'),
        width: 140,
        sortable: true,
        filter: true,
      }] : []),
      ...(showParentTradeNumber ? [{
        headerName: t('commodities.positions.grid.parentTradeNumber', 'Parent Trade #'),
        valueGetter: (params: any) => params.data?.internalTradeRef?.parentTradeNumber || '',
        width: 140,
        sortable: true,
        filter: true,
      }] : []),
      { field: 'workflowState',        headerName: t('commodities.positions.grid.workflowState'),        width: 110 },
      { field: 'isInternalTrade',      headerName: t('commodities.positions.grid.isInternalTrade', 'Internal'),
        width: 90, hide: true,
        valueFormatter: (params: any) => params.value ? 'Yes' : 'No',
      },
      { field: 'LegacyReference1',     headerName: t('commodities.positions.grid.legacyReference1'),     width: 130 },
      { field: 'OurParty',             headerName: t('commodities.positions.grid.ourParty'),             width: 180 },
      { field: 'CounterpartyName',     headerName: t('commodities.positions.grid.counterpartyName'),     width: 200 },
      { field: 'ProductType',          headerName: t('commodities.positions.grid.productType'),          width: 110 },
      { field: 'UnderlyingName',       headerName: t('commodities.positions.grid.underlyingName'),       width: 160 },
      { field: 'Direction',            headerName: t('commodities.positions.grid.direction'),            width: 90 },
      { field: 'TradeDate',            headerName: t('commodities.positions.grid.tradeDate'),            width: 120, valueFormatter: dateFmt },
      { field: 'Lots',                 headerName: t('commodities.positions.grid.lots'),                 width: 80,  type: 'numericColumn', valueFormatter: numFmt(0) },
      { field: 'Price',                headerName: t('commodities.positions.grid.price'),                width: 110, type: 'numericColumn', valueFormatter: numFmt(4) },
      { field: 'Quantity',             headerName: t('commodities.positions.grid.quantity'),             width: 120, type: 'numericColumn', valueFormatter: numFmt(4) },
      { field: 'Currency',             headerName: t('commodities.positions.grid.currency'),             width: 90 },
      { field: 'UnitOfMeasure',        headerName: t('commodities.positions.grid.unitOfMeasure'),        width: 90 },
      { field: 'MaturityDate',         headerName: t('commodities.positions.grid.maturityDate'),         width: 120, valueFormatter: dateFmt },
      { field: 'FixingReference',      headerName: t('commodities.positions.grid.fixingReference'),      width: 110 },

      // Settlement/fixing columns — visible on expiry page, hidden elsewhere
      ...(showSettlementColumns ? [{
        headerName: t('commodities.positions.grid.fixingStatus'),
        field: 'SettlementPrice',
        colId: 'fixingStatus',
        width: 100,
        sortable: true,
        filter: true,
        valueGetter: (params: any) => params.data?.SettlementPrice != null
          ? t('commodities.positions.grid.fixingStatusFixed')
          : t('commodities.positions.grid.fixingStatusPending'),
        cellStyle: (params: any) => {
          const isFixed = params.data?.SettlementPrice != null;
          return {
            color: isFixed ? '#22c55e' : '#f59e0b',
            fontWeight: 600,
            textAlign: 'center',
          };
        },
      }] : []),
      { field: 'SettlementPrice',      headerName: t('commodities.positions.grid.settlementPrice'),     width: 120, hide: !showSettlementColumns, type: 'numericColumn', valueFormatter: numFmt(4) },
      { field: 'SettlementAmount',     headerName: t('commodities.positions.grid.settlementAmount'),     width: 130, hide: !showSettlementColumns, type: 'numericColumn', valueFormatter: numFmt(2) },
      { field: 'SettlementCurrency',   headerName: t('commodities.positions.grid.settlementCurrency'),   width: 100, hide: !showSettlementColumns },

      { field: 'ExecutionType',        headerName: t('commodities.positions.grid.executionType'),        width: 120 },
      { field: 'VenueName',            headerName: t('commodities.positions.grid.venueName'),            width: 160 },
      { field: 'ExchangeContractName', headerName: t('commodities.positions.grid.exchangeContractName'), width: 180 },
      { field: 'ContractMonth',        headerName: t('commodities.positions.grid.contractMonth'),        width: 130 },
      { field: 'OptionType',           headerName: t('commodities.positions.grid.optionType'),           width: 110 },
      { field: 'Premium',              headerName: t('commodities.positions.grid.premium'),              width: 100, type: 'numericColumn', valueFormatter: numFmt(4) },

      // Hidden columns (available via column chooser)
      { field: 'UnderlyingCode',       headerName: t('commodities.positions.grid.underlyingCode'),       width: 140, hide: true },
      { field: 'VenueCode',            headerName: t('commodities.positions.grid.venueCode'),            width: 110, hide: true },
      { field: 'ExchangeContractCode', headerName: t('commodities.positions.grid.exchangeContractCode'), width: 130, hide: true },
      { field: 'ValueDate',            headerName: t('commodities.positions.grid.valueDate'),            width: 120, hide: true, valueFormatter: dateFmt },
      { field: 'PaymentDate',          headerName: t('commodities.positions.grid.paymentDate'),          width: 120, hide: true, valueFormatter: dateFmt },
      { field: 'LegacyReference2',     headerName: t('commodities.positions.grid.legacyReference2'),     width: 130, hide: true },
      { field: 'SourceSystem',         headerName: t('commodities.positions.grid.sourceSystem'),         width: 120, hide: true },
      { field: 'SourceChannel',        headerName: t('commodities.positions.grid.sourceChannel'),        width: 120, hide: true },
      { field: 'Grouping1',            headerName: t('commodities.positions.grid.grouping1'),            width: 120, hide: true },
      { field: 'Grouping2',            headerName: t('commodities.positions.grid.grouping2'),            width: 120, hide: true },
      { field: 'Grouping3',            headerName: t('commodities.positions.grid.grouping3'),            width: 120, hide: true },
      { field: 'HedgeObject',          headerName: t('commodities.positions.grid.hedgeObject'),          width: 130, hide: true },
      { field: 'Notes',                headerName: t('commodities.positions.grid.notes'),                width: 200, hide: true },
      {
        field: 'pendingAllocations',
        headerName: t('commodities.positions.grid.pendingAllocations'),
        width: 200,
        hide: true,
        valueFormatter: (params: any): string => {
          const arr = params.value;
          if (!Array.isArray(arr) || arr.length === 0) return '';
          if (arr.length === 1) {
            const a = arr[0];
            return `${a.entityName} (${a.percentage}%)`;
          }
          return t('commodities.positions.grid.pendingAllocationsCount', { count: arr.length });
        },
      },
    ];
  }, [t, i18n.language, showMirrorId, showParentTradeNumber, showSettlementColumns, suppressDefaultSort]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    filterParams: {
      buttons: ['reset'],
    },
  }), []);

  // External filter: trade number search + maturity date
  const isExternalFilterPresent = useMemo(() => {
    return () => !!(tradeNumberSearchRef.current || maturityDateFromRef.current);
  }, []);

  const doesExternalFilterPass = useMemo(() => {
    return (node: any) => {
      const search = tradeNumberSearchRef.current;
      if (search) {
        const tradeNum = String(node.data?.TradeNumber || '');
        const parentNum = String(node.data?.internalTradeRef?.parentTradeNumber || '');
        if (!tradeNum.includes(search) && !parentNum.includes(search)) return false;
      }
      const minDate = maturityDateFromRef.current;
      if (minDate) {
        const maturity = node.data?.MaturityDate || '';
        if (maturity && maturity < minDate) return false;
      }
      return true;
    };
  }, []);

  if (loading) {
    return (
      <div className="trade-grid-loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '200px' }}>
        <div className="app-loading-spinner" />
        <p>{t('commodities.positions.loading')}</p>
      </div>
    );
  }

  return (
    <div className="ag-theme-alpine-dark trade-grid" style={{ width: '100%', height: '100%' }}>
      <AgGridReact
        ref={gridRef}
        rowData={trades}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        localeText={localeText}
        animateRows={true}
        pagination={true}
        paginationPageSize={50}
        rowSelection={multiSelect ? 'multiple' : 'single'}
        suppressCellFocus={true}
        getRowId={(params) => params.data.id}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={doesExternalFilterPass}
        onSelectionChanged={(event) => {
          if (onMultiSelectionChanged) {
            onMultiSelectionChanged(event.api.getSelectedRows());
          }
        }}
      />
    </div>
  );
};

export default CommodityTradesGrid;
